import { Button, Colors, ControlGroup, INumericInputProps, Tag, Position } from '@blueprintjs/core'
import * as React from 'react'
import { GutterTooltipError } from '../../routes/products_v5/components/JoinedForm/GutterTooltipError'
import { ProductFormGroup } from '../../routes/products_v5/components/JoinedForm/ProductFormGroup'
import NullSafeNumericInput from '../../components/NullSafeNumericInput'

export interface WeightInputProps extends INumericInputProps {
  formGroupClassName?: string
  value: string
  showRequiredLabel?: boolean
  minorStepSize: number
  label?: string
  labelInfo?: React.ReactNode
  labelFor?: string
  style?: React.CSSProperties
  scaleConnected: boolean
  errorTooltipOffset?: number
  error?: string
}

interface DynamicWeightInputProps extends WeightInputProps {
  scaleWeight: number
  scaleName: string
}

const WeightInput: React.FC<DynamicWeightInputProps> = props => {
  const { scaleWeight, scaleName, ...otherProps } = props

  return props.scaleConnected ? (
    <DynamicWeightInput {...otherProps} scaleWeight={scaleWeight} scaleName={scaleName || 'Unknown Scale'} />
  ) : (
    <StaticWeightInput {...otherProps} />
  )
}

export default WeightInput

const DynamicWeightInput: React.FC<DynamicWeightInputProps> = props => {
  const onClick = () => {
    props.onValueChange(props.scaleWeight, `${props.scaleWeight}`, null)
    analytics.track('Scale Weight Copied', {
      scale: props.scaleName
    })
  }

  return (
    <ProductFormGroup
      title="" // see label
      label={
        // this will override title
        <span>
          <span>{props.label}</span>{' '}
          {props.scaleWeight !== null && <span style={{ color: Colors.GRAY4 }}>({props.scaleWeight})</span>}
        </span>
      }
      labelInfo={props.labelInfo}
      error={props.error}
      showRequiredLabel={props.showRequiredLabel}
      className={props.formGroupClassName}
      helperText={`Reading from scale ${props.scaleName}`}
      disabled={props.disabled}
    >
      <GutterTooltipError isOpen={!!props.error} offsetPx={props.errorTooltipOffset} position={Position.RIGHT}>
        <ControlGroup>
          <NullSafeNumericInput
            preventNegative
            {...props}
            value={props.value}
            disabled={props.disabled}
            rightElement={<Tag minimal>LBS</Tag>}
            minorStepSize={0.001}
            min={0}
          />
          <Button onClick={onClick} disabled={props.disabled}>
            <span className="bp3-icon bp3-icon-feed">
              <svg
                style={{ width: 16, height: 16 }}
                aria-hidden="true"
                data-prefix="fas"
                data-icon="balance-scale"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 512"
              >
                <path
                  fill="currentColor"
                  d="M256 336h-.02c0-16.18 1.34-8.73-85.05-181.51-17.65-35.29-68.19-35.36-85.87 0C-2.06 328.75.02 320.33.02 336H0c0 44.18 57.31 80 128 80s128-35.82 128-80zM128 176l72 144H56l72-144zm511.98 160c0-16.18 1.34-8.73-85.05-181.51-17.65-35.29-68.19-35.36-85.87 0-87.12 174.26-85.04 165.84-85.04 181.51H384c0 44.18 57.31 80 128 80s128-35.82 128-80h-.02zM440 320l72-144 72 144H440zm88 128H352V153.25c23.51-10.29 41.16-31.48 46.39-57.25H528c8.84 0 16-7.16 16-16V48c0-8.84-7.16-16-16-16H383.64C369.04 12.68 346.09 0 320 0s-49.04 12.68-63.64 32H112c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h129.61c5.23 25.76 22.87 46.96 46.39 57.25V448H112c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h416c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16z"
                />
              </svg>
            </span>
          </Button>
        </ControlGroup>
      </GutterTooltipError>
    </ProductFormGroup>
  )
}

const StaticWeightInput: React.FC<WeightInputProps> = props => {
  // Avoid console errors b/c NumericInput passes all props into some DOM element
  const {
    formGroupClassName,
    scaleConnected,
    showRequiredLabel,
    errorTooltipOffset,
    labelInfo,
    labelFor,
    ...otherProps
  } = props

  const safeLabelFor = labelFor?.replace(/\W/g, '') // generate a valid DOM ID so that elements are accessible in react tests
  const maybeId: Record<string, any> = {}
  if (safeLabelFor) {
    maybeId.id = safeLabelFor
  }

  return (
    <ProductFormGroup
      title={props.label}
      error={props.error}
      showRequiredLabel={showRequiredLabel}
      labelInfo={props.labelInfo}
      className={formGroupClassName}
      labelFor={safeLabelFor}
    >
      <GutterTooltipError isOpen={!!props.error} offsetPx={errorTooltipOffset} position={Position.RIGHT}>
        <ControlGroup>
          <NullSafeNumericInput
            min={0}
            preventNegative
            {...maybeId}
            {...otherProps}
            value={props.value}
            rightElement={<Tag minimal>LBS</Tag>}
          />
        </ControlGroup>
      </GutterTooltipError>
    </ProductFormGroup>
  )
}
